import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../config/api';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Favorite,
  LockOpen,
  AccessTime,
  Block,
  CardGiftcard,
  Search,
} from '@mui/icons-material';
import type {
  CupidCardAdminStatsDto,
  CupidCardBatchResultDto,
  CupidCardAdminListDto,
  CupidCardAdminDetailDto,
} from '../../types';
import {
  fetchCupidCardStats,
  fetchCupidCardBatchResult,
  fetchCupidCards,
  fetchCupidCardDetail,
  forceExpireCard,
  grantHearts,
} from './api/cupidCardAdminApi';
import ManualCardIssueDialog from './ManualCardIssueDialog';
import CupidCardDetailModal from './components/CupidCardDetailModal';

// ─── Types ───────────────────────────────────────────────────────────────────

type DisplayStatus = 'LOCKED' | 'OPENED' | 'EXPIRED_EXTENDABLE' | 'DEAD';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getEffectiveStatus(card: CupidCardAdminListDto): DisplayStatus {
  if (card.isDead) return 'DEAD';
  if (card.isExpiredExtendable) return 'EXPIRED_EXTENDABLE';
  return card.status as DisplayStatus;
}

function getISOWeekString(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // ISO: Mon=1 … Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // shift to Thursday of current week
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function weekLabelToIso(label: string): string | undefined {
  // If already an ISO week string (e.g. "2025-W07"), return as-is
  if (/^\d{4}-W\d{2}$/.test(label)) return label;
  const now = new Date();
  if (label === '이번 주') return getISOWeekString(now);
  if (label === '지난 주') {
    const lastWeek = new Date(now);
    lastWeek.setDate(lastWeek.getDate() - 7);
    return getISOWeekString(lastWeek);
  }
  return undefined;
}

// Generate the last 8 weeks as ISO week strings (most recent first)
const WEEK_OPTIONS: string[] = Array.from({ length: 8 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - i * 7);
  return getISOWeekString(d);
});

// ─── Status badge helper ──────────────────────────────────────────────────────

const STATUS_STYLES: Record<DisplayStatus, { bgcolor: string; color: string; label: string }> = {
  LOCKED: { bgcolor: '#E3F2FD', color: '#1565C0', label: 'LOCKED' },
  OPENED: { bgcolor: '#E8F5E9', color: '#2E7D32', label: 'OPENED' },
  EXPIRED_EXTENDABLE: { bgcolor: '#FFF3E0', color: '#E65100', label: 'EXPIRED-EXTENDABLE' },
  DEAD: { bgcolor: '#F5F5F5', color: '#757575', label: 'DEAD' },
};

const StatusChip: React.FC<{ status: DisplayStatus }> = ({ status }) => {
  const style = STATUS_STYLES[status] ?? STATUS_STYLES.DEAD;
  return (
    <Chip
      label={style.label}
      size="small"
      sx={{ bgcolor: style.bgcolor, color: style.color, fontWeight: 600 }}
    />
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

const AdminCupidCardsPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Auth guard
  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
    }
  }, [user, isLoading, navigate]);

  // ── State ─────────────────────────────────────────────────────────────────
  const [selectedWeek, setSelectedWeek] = useState(() => WEEK_OPTIONS[0]);
  const [tabValue, setTabValue] = useState(0); // 0 = 카드 관리, 1 = 하트 소비 내역
  const [searchQuery, setSearchQuery] = useState('');            // text field input (not submitted yet)
  const [activeSearchQuery, setActiveSearchQuery] = useState(''); // committed on Search click / Enter
  const [searchTrigger, setSearchTrigger] = useState(0);          // increment to force re-fetch
  const [statusFilter, setStatusFilter] = useState('전체');
  const [weekFilter, setWeekFilter] = useState(() => WEEK_OPTIONS[0]);
  const [openFilter, setOpenFilter] = useState('전체');
  const [extendFilter, setExtendFilter] = useState('전체');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);

  const [stats, setStats] = useState<CupidCardAdminStatsDto | null>(null);
  const [batchResult, setBatchResult] = useState<CupidCardBatchResultDto | null>(null);
  const [cards, setCards] = useState<CupidCardAdminListDto[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [forceExpireLoading, setForceExpireLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [selectedCard, setSelectedCard] = useState<CupidCardAdminDetailDto | null>(null);
  const [forceExpireTarget, setForceExpireTarget] = useState<number | null>(null);

  // 하트 수동 지급 다이얼로그
  const [heartGrantOpen, setHeartGrantOpen] = useState(false);
  const [heartGrantUserId, setHeartGrantUserId] = useState('');
  const [heartGrantAmount, setHeartGrantAmount] = useState('');
  const [heartGrantReason, setHeartGrantReason] = useState('');
  const [heartGrantLoading, setHeartGrantLoading] = useState(false);

  // 카드 수동 발급 다이얼로그
  const [cardIssueOpen, setCardIssueOpen] = useState(false);

  // 하트 소비 내역 탭
  const [consumeTransactions, setConsumeTransactions] = useState<any[]>([]);
  const [consumeTotalElements, setConsumeTotalElements] = useState(0);
  const [consumePage, setConsumePage] = useState(0);
  const [consumeLoading, setConsumeLoading] = useState(false);
  const CONSUME_PAGE_SIZE = 20;

  // ── Date formatter ────────────────────────────────────────────────────────
  const formatDateTime = (isoStr: string) =>
    new Date(isoStr).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  // ── API fetch functions ───────────────────────────────────────────────────

  const loadStats = useCallback(async (week: string) => {
    setStatsLoading(true);
    try {
      const [statsData, batchData] = await Promise.all([
        fetchCupidCardStats(week),
        fetchCupidCardBatchResult(week),
      ]);
      setStats(statsData);
      setBatchResult(batchData);
    } catch (err: any) {
      setErrorMsg(err?.message ?? '통계 데이터 로딩 실패');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCards = useCallback(async () => {
    setTableLoading(true);
    try {
      const openedParam =
        openFilter === '오픈됨' ? true : openFilter === '미오픈' ? false : undefined;
      const extendedParam =
        extendFilter === '연장됨' ? true : extendFilter === '미연장' ? false : undefined;
      const isoWeek = weekLabelToIso(weekFilter);

      const data = await fetchCupidCards({
        status: statusFilter,
        week: isoWeek,
        opened: openedParam,
        extended: extendedParam,
        searchKeyword: activeSearchQuery,
        page,
        size: rowsPerPage,
      });
      setCards(data.content);
      setTotalElements(data.totalElements);
    } catch (err: any) {
      setErrorMsg(err?.message ?? '카드 목록 로딩 실패');
    } finally {
      setTableLoading(false);
    }
  }, [statusFilter, weekFilter, openFilter, extendFilter, activeSearchQuery, page, rowsPerPage, searchTrigger]);

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') {
      loadStats(selectedWeek);
    }
  }, [isLoading, user, selectedWeek, loadStats]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN') {
      loadCards();
    }
  }, [isLoading, user, loadCards]);

  const loadConsumeTransactions = useCallback(async () => {
    setConsumeLoading(true);
    try {
      const data = await apiClient.getHeartTransactions({
        page: consumePage,
        size: CONSUME_PAGE_SIZE,
        type: 'CONSUME',
      });
      setConsumeTransactions(data.transactions);
      setConsumeTotalElements(data.totalElements);
    } catch (err: any) {
      setErrorMsg(err?.message ?? '하트 소비 내역 로딩 실패');
    } finally {
      setConsumeLoading(false);
    }
  }, [consumePage]);

  useEffect(() => {
    if (!isLoading && user?.role === 'ADMIN' && tabValue === 1) {
      loadConsumeTransactions();
    }
  }, [isLoading, user, tabValue, loadConsumeTransactions]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery); // commit input
    setPage(0);
    setSearchTrigger((t) => t + 1); // ensure effect re-fires even if other deps unchanged
  };

  const handleHeartGrant = async () => {
    const userId = Number(heartGrantUserId);
    const amount = Number(heartGrantAmount);
    if (!userId || !amount || !heartGrantReason.trim()) {
      setErrorMsg('사용자 ID, 지급 수량, 사유를 모두 입력해주세요.');
      return;
    }
    setHeartGrantLoading(true);
    try {
      const result = await grantHearts(userId, amount, heartGrantReason.trim());
      setSuccessMsg(
        `사용자 ${result.userId}에게 하트 ${result.grantedAmount}개 지급 완료 (잔액: ${result.balanceAfter})`
      );
      setHeartGrantOpen(false);
      setHeartGrantUserId('');
      setHeartGrantAmount('');
      setHeartGrantReason('');
    } catch (err: any) {
      setErrorMsg(err?.message ?? '하트 지급 실패');
    } finally {
      setHeartGrantLoading(false);
    }
  };

  const handleViewDetail = async (cardId: number) => {
    try {
      const detail = await fetchCupidCardDetail(cardId);
      setSelectedCard(detail);
    } catch (err: any) {
      setErrorMsg(err?.message ?? '카드 상세 로딩 실패');
    }
  };

  const handleForceExpireConfirm = async () => {
    if (forceExpireTarget === null) return;
    setForceExpireLoading(true);
    try {
      await forceExpireCard(forceExpireTarget, '관리자 강제 만료');
      setForceExpireTarget(null);
      setSelectedCard(null);
      setSuccessMsg('카드가 강제 만료되었습니다.');
      await Promise.all([loadStats(selectedWeek), loadCards()]);
    } catch (err: any) {
      setErrorMsg(err?.message ?? '강제 만료 실패');
    } finally {
      setForceExpireLoading(false);
    }
  };

  // ── Loading / auth guard ──────────────────────────────────────────────────
  if (isLoading || !user || user.role !== 'ADMIN') {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h4">큐피트 카드 관리</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CardGiftcard />}
              onClick={() => setCardIssueOpen(true)}
              sx={{ color: 'primary.main', borderColor: 'primary.main', '&:hover': { borderColor: 'primary.dark' } }}
            >
              카드 수동 발급
            </Button>
            <Button
              variant="outlined"
              startIcon={<Favorite />}
              onClick={() => setHeartGrantOpen(true)}
              sx={{ color: 'error.main', borderColor: 'error.main', '&:hover': { borderColor: 'error.dark' } }}
            >
              하트 수동 지급
            </Button>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_e, v) => setTabValue(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="카드 관리" />
          <Tab label="하트 소비 내역" />
        </Tabs>

        {/* ── Tab 1: 하트 소비 내역 ─────────────────────────────────── */}
        {tabValue === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                하트 소비 내역
              </Typography>

              {errorMsg && (
                <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ mb: 2 }}>
                  {errorMsg}
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>거래 ID</TableCell>
                      <TableCell>거래 유형</TableCell>
                      <TableCell align="right">차감</TableCell>
                      <TableCell align="right">변동 전</TableCell>
                      <TableCell align="right">변동 후</TableCell>
                      <TableCell>사유</TableCell>
                      <TableCell>일시</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consumeLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : consumeTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          소비 내역이 없습니다.
                        </TableCell>
                      </TableRow>
                    ) : (
                      consumeTransactions.map((tx) => {
                        const isOpen = tx.transactionType === 'CONSUME_CUPID_CARD_OPEN';
                        const isExtend = tx.transactionType === 'CONSUME_CUPID_CARD_EXTEND';
                        return (
                          <TableRow key={tx.id} hover>
                            <TableCell>{tx.id}</TableCell>
                            <TableCell>
                              <Chip
                                label={isOpen ? '카드 오픈' : isExtend ? '카드 연장' : tx.transactionTypeTitle ?? tx.transactionType}
                                size="small"
                                sx={{
                                  bgcolor: isOpen ? '#E3F2FD' : isExtend ? '#FFF3E0' : '#F5F5F5',
                                  color: isOpen ? '#1565C0' : isExtend ? '#E65100' : '#757575',
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                              {tx.amount} ♥
                            </TableCell>
                            <TableCell align="right">{tx.balanceBefore}</TableCell>
                            <TableCell align="right">{tx.balanceAfter}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                                {tx.reason ?? '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {new Date(tx.createdTime).toLocaleString('ko-KR', {
                                month: '2-digit', day: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={consumeTotalElements}
                page={consumePage}
                onPageChange={(_e, newPage) => setConsumePage(newPage)}
                rowsPerPage={CONSUME_PAGE_SIZE}
                onRowsPerPageChange={() => {}}
                rowsPerPageOptions={[20]}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} / 총 ${count === -1 ? '?' : count}건`
                }
              />
            </CardContent>
          </Card>
        )}

        {tabValue === 0 && (
          <>
        {/* ── Section A: Stats Cards (6) ───────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(6, 1fr)',
            },
            gap: 2,
            mb: 4,
          }}
        >
          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <CardGiftcard sx={{ fontSize: 36, color: 'text.primary', mb: 0.5 }} />
              <Typography variant="h5" color="text.primary">
                {statsLoading ? '-' : (stats?.totalCards ?? '-')}
              </Typography>
              <Typography variant="caption" color="text.secondary">총 카드 수</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <LockOpen sx={{ fontSize: 36, color: 'success.main', mb: 0.5 }} />
              <Typography variant="h5" color="success.main">
                {statsLoading || !stats
                  ? '-'
                  : `${stats.openedCount} (${(stats.openedRate * 100).toFixed(1)}%)`}
              </Typography>
              <Typography variant="caption" color="text.secondary">OPENED</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <AccessTime sx={{ fontSize: 36, color: 'warning.main', mb: 0.5 }} />
              <Typography variant="h5" color="warning.main">
                {statsLoading ? '-' : (stats?.expiredExtendableCount ?? '-')}
              </Typography>
              <Typography variant="caption" color="text.secondary">EXPIRED-EXTENDABLE</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Block sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
              <Typography variant="h5" color="text.disabled">
                {statsLoading ? '-' : (stats?.deadCount ?? '-')}
              </Typography>
              <Typography variant="caption" color="text.secondary">DEAD</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Favorite sx={{ fontSize: 36, color: 'error.main', mb: 0.5 }} />
              <Typography variant="h5" color="error.main">
                {statsLoading || !stats ? '-' : `${stats.heartConsumedTotal} \u2665`}
              </Typography>
              <Typography variant="caption" color="text.secondary">하트 소비</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <AccessTime sx={{ fontSize: 36, color: 'secondary.main', mb: 0.5 }} />
              <Typography variant="h5" color="secondary.main">
                {statsLoading || !stats
                  ? '-'
                  : `${(stats.extendRate * 100).toFixed(1)}%`}
              </Typography>
              <Typography variant="caption" color="text.secondary">연장률</Typography>
            </CardContent>
          </Card>
        </Box>

        {/* ── Section B: 주간 배치 현황 ────────────────────────────────── */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6">주간 배치 현황</Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>주차 선택</InputLabel>
                <Select
                  value={selectedWeek}
                  label="주차 선택"
                  onChange={(e) => setSelectedWeek(e.target.value)}
                >
                  {WEEK_OPTIONS.map((w, i) => (
                    <MenuItem key={w} value={w}>
                      {i === 0 ? `이번 주 (${w})` : i === 1 ? `지난 주 (${w})` : w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>배치 실행:</strong>{' '}
                  {batchResult?.executedAt ? formatDateTime(batchResult.executedAt) : '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>처리 결과:</strong>{' '}
                  대상 {batchResult?.recipientCount ?? '-'}명 | 발급 {batchResult?.issuedCardCount ?? '-'}장 | 실패 {batchResult?.failedRecipientCount ?? '-'}명
                </Typography>
                <Typography variant="body2">
                  <strong>배치 상태:</strong>{' '}
                  {(() => {
                    const s = batchResult?.batchStatus;
                    if (!s || s === 'NOT_EXECUTED') return <span style={{ color: '#9E9E9E' }}>미실행</span>;
                    if (s === 'COMPLETED') return <span style={{ color: '#2E7D32' }}>완료</span>;
                    if (s === 'FAILED') return <span style={{ color: '#C62828' }}>실패</span>;
                    return <span>{s}</span>;
                  })()}
                </Typography>
                <Typography variant="body2">
                  <strong>종료:</strong>{' '}
                  {batchResult?.endedAt ? formatDateTime(batchResult.endedAt) : '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ── Section C: 카드 목록 테이블 ─────────────────────────────── */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              카드 목록
            </Typography>

            {/* Filter bar */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                size="small"
                label="수신자 이름/ID 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>상태</InputLabel>
                <Select
                  value={statusFilter}
                  label="상태"
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="전체">전체</MenuItem>
                  <MenuItem value="LOCKED">LOCKED</MenuItem>
                  <MenuItem value="OPENED">OPENED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>주차</InputLabel>
                <Select
                  value={weekFilter}
                  label="주차"
                  onChange={(e) => { setWeekFilter(e.target.value); setPage(0); }}
                >
                  {WEEK_OPTIONS.map((w, i) => (
                    <MenuItem key={w} value={w}>
                      {i === 0 ? `이번 주 (${w})` : i === 1 ? `지난 주 (${w})` : w}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>오픈 여부</InputLabel>
                <Select
                  value={openFilter}
                  label="오픈 여부"
                  onChange={(e) => { setOpenFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="전체">전체</MenuItem>
                  <MenuItem value="오픈됨">오픈됨</MenuItem>
                  <MenuItem value="미오픈">미오픈</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>연장 여부</InputLabel>
                <Select
                  value={extendFilter}
                  label="연장 여부"
                  onChange={(e) => { setExtendFilter(e.target.value); setPage(0); }}
                >
                  <MenuItem value="전체">전체</MenuItem>
                  <MenuItem value="연장됨">연장됨</MenuItem>
                  <MenuItem value="미연장">미연장</MenuItem>
                </Select>
              </FormControl>

              <Button variant="contained" onClick={handleSearch}>
                검색
              </Button>
            </Box>

            {/* Error alert for table */}
            {errorMsg && (
              <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ mb: 2 }}>
                {errorMsg}
              </Alert>
            )}

            {/* Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>카드 ID</TableCell>
                    <TableCell>수신자 (이름 + ID)</TableCell>
                    <TableCell>발신자(타겟)</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell>발급일</TableCell>
                    <TableCell>만료일</TableCell>
                    <TableCell>오픈</TableCell>
                    <TableCell>연장</TableCell>
                    <TableCell align="center">액션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : cards.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        데이터가 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    cards.map((card) => (
                      <TableRow key={card.cardId} hover>
                        <TableCell>{card.cardId}</TableCell>
                        <TableCell>
                          {card.recipientName}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            (ID: {card.recipientUserId})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {card.targetName}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            (ID: {card.targetUserId})
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={getEffectiveStatus(card)} />
                        </TableCell>
                        <TableCell>{formatDateTime(card.issuedAt)}</TableCell>
                        <TableCell>{formatDateTime(card.expiresAt)}</TableCell>
                        <TableCell>
                          {card.openedTime
                            ? `\u2705 ${formatDateTime(card.openedTime)}`
                            : '\u274C'}
                        </TableCell>
                        <TableCell>{card.extendCount >= 1 ? '\u2705' : '\u274C'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDetail(card.cardId)}
                            >
                              상세보기
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => setForceExpireTarget(card.cardId)}
                            >
                              강제만료
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={() => {}}
              rowsPerPageOptions={[20]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} / 총 ${count === -1 ? '?' : count}건`
              }
            />
          </CardContent>
        </Card>
          </>
        )}
      </Box>

      {/* ── Section 6: Card Detail Modal ──────────────────────────────── */}
      {selectedCard !== null && (
        <CupidCardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          onForceExpire={(cardId) => setForceExpireTarget(cardId)}
        />
      )}

      {/* ── Section 7-1: Force Expire Confirmation Dialog ─────────────── */}
      <Dialog
        open={forceExpireTarget !== null}
        onClose={() => setForceExpireTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>카드 강제 만료</DialogTitle>
        <DialogContent>
          <Typography>
            정말 이 카드를 강제 만료하시겠습니까? 복구할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceExpireTarget(null)} disabled={forceExpireLoading}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceExpireConfirm}
            disabled={forceExpireLoading}
          >
            {forceExpireLoading ? <CircularProgress size={16} color="inherit" /> : '강제 만료'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 하트 수동 지급 Dialog ─────────────────────────────────────── */}
      <Dialog
        open={heartGrantOpen}
        onClose={() => !heartGrantLoading && setHeartGrantOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>하트 수동 지급</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="대상 사용자 ID"
            type="number"
            value={heartGrantUserId}
            onChange={(e) => setHeartGrantUserId(e.target.value)}
            size="small"
            fullWidth
            disabled={heartGrantLoading}
          />
          <TextField
            label="지급 수량"
            type="number"
            value={heartGrantAmount}
            onChange={(e) => setHeartGrantAmount(e.target.value)}
            size="small"
            fullWidth
            disabled={heartGrantLoading}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="사유 메모"
            value={heartGrantReason}
            onChange={(e) => setHeartGrantReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            disabled={heartGrantLoading}
            placeholder="예: 이벤트 보상, 오류 보상 등"
          />
          {heartGrantUserId && heartGrantAmount && (
            <Typography variant="body2" color="text.secondary">
              사용자 ID {heartGrantUserId}에게 하트 {heartGrantAmount}개를 지급합니다.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeartGrantOpen(false)} disabled={heartGrantLoading}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleHeartGrant}
            disabled={heartGrantLoading}
            startIcon={heartGrantLoading ? <CircularProgress size={16} /> : <Favorite />}
          >
            지급
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 카드 수동 발급 Dialog ─────────────────────────────────────── */}
      <ManualCardIssueDialog
        open={cardIssueOpen}
        onClose={() => setCardIssueOpen(false)}
        onSuccess={async (msg) => {
          setSuccessMsg(msg);
          await Promise.all([loadStats(selectedWeek), loadCards()]);
        }}
        onError={(msg) => setErrorMsg(msg)}
      />

      {/* ── Success Snackbar ──────────────────────────────────────────── */}
      <Snackbar
        open={successMsg !== null}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMsg(null)} severity="success" sx={{ width: '100%' }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default AdminCupidCardsPage;
